/**
 * SSO (Single Sign-On) handler
 */
import { pushFunnelEvent } from '@utils/dataLayer';
import BaseHandler from './BaseHandler';
import DOMUtils from '../utils/DOMUtils';
import { FIELD_SELECTORS } from '../constants';

class SSOHandler extends BaseHandler {
  /**
   * Handle SSO callback from OAuth provider
   */
  async handleSSOCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const ssoCallback = urlParams.get('sso_callback');

    // Only process if this is an SSO callback
    if (!ssoCallback || !code || !state) {
      return false;
    }

    try {
      // Store state from URL if not already stored (fallback for direct link flow)
      const storedState = sessionStorage.getItem('azds_sso_state');
      if (!storedState) {
        // Store state from URL for validation (state is validated on server side)
        sessionStorage.setItem('azds_sso_state', state);
      }

      // Validate stored state
      const currentStoredState = sessionStorage.getItem('azds_sso_state');
      if (!currentStoredState || currentStoredState !== state) {
        this.showSSOError(this.messages.getMessage('error_generic'));
        this.cleanupSSOCallback();
        return true;
      }

      // Get redirect URI (current page without callback params)
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('state');
      currentUrl.searchParams.delete('sso_callback');
      const redirectUri = currentUrl.toString();

      // Call SSO callback endpoint
      const { response, data } = await this.api.ssoCallback(code, state, redirectUri);

      if (response.ok) {
        // Clean up URL params
        this.cleanupSSOCallback();

        // Check if registration is required
        if (data.needs_registration && data.sso_token && data.user_data) {
          // Store SSO data for registration
          sessionStorage.setItem('azds_sso_token', data.sso_token);

          // Show SSO registration form with pre-filled data
          this.stepNavigator.goToStep('register-sso');

          // Track Sign Up step 1: Start for Google
          pushFunnelEvent({
            section: 'Sign Up',
            step: 1,
            label: 'Sign Up - Start',
            method: 'Google',
          });

          // Pre-fill form fields from Google data
          const emailInput = this.form.querySelector(FIELD_SELECTORS.regSsoEmail);
          const firstNameInput = this.form.querySelector(FIELD_SELECTORS.regSsoFirstName);
          const lastNameInput = this.form.querySelector(FIELD_SELECTORS.regSsoLastName);

          if (emailInput) {
            emailInput.value = data.user_data.email || '';
          }

          if (firstNameInput && data.user_data.first_name) {
            firstNameInput.value = data.user_data.first_name;
          }

          if (lastNameInput && data.user_data.last_name) {
            lastNameInput.value = data.user_data.last_name;
          }

          return true;
        }

        // Track successful Google login
        pushFunnelEvent({
          section: 'Sign In',
          step: 2,
          label: 'Sign In - Success',
          method: 'Google',
        });

        // Redirect on success (existing user login)
        const redirectUrl = this.getRedirectUrl();
        if (redirectUrl) {
          localStorage.removeItem('azds_sso_redirect');
          window.location.href = redirectUrl;
        } else if (data.redirect) {
          localStorage.removeItem('azds_sso_redirect');
          window.location.href = data.redirect;
        } else {
          window.location.reload();
        }
      } else {
        const errorMessage = this.messages.getMessageFromApi(data.message, {}) || this.messages.getMessage('error_generic');
        this.showSSOError(errorMessage);
        this.cleanupSSOCallback();
      }

      return true;
    } catch (error) {
      const errorMessage = this.messages.getMessageFromApi(error.message, {}) || this.messages.getMessage('error_connection');
      this.showSSOError(errorMessage);
      this.cleanupSSOCallback();
      return true;
    }
  }

  /**
   * Show SSO error message
   */
  showSSOError(message) {
    let errorElement = this.form.querySelector('.auth-form__sso-error');
    if (!errorElement) {
      // Create error element if it doesn't exist
      errorElement = document.createElement('div');
      errorElement.className = 'auth-form__message auth-form__sso-error';
      const ssoContainer = this.form.querySelector('.auth-form__sso');
      if (ssoContainer) {
        ssoContainer.insertBefore(errorElement, ssoContainer.firstChild);
      }
    }
    DOMUtils.showMessage(errorElement, message, 'error');
  }

  /**
   * Clean up SSO callback state
   */
  // eslint-disable-next-line class-methods-use-this
  cleanupSSOCallback() {
    sessionStorage.removeItem('azds_sso_state');
    // Remove callback params from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('sso_callback');
    window.history.replaceState({}, '', url.toString());
  }
}

export default SSOHandler;
